import React, { useState, useEffect, useMemo } from "react";
import { notification } from "antd";
import { FinetuningService } from "../../services/FinetuningService";
import { usePermissions } from "../../PermissionsProvider";
import { Model } from "../../model_gen/model";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { PlusIcon } from "lucide-react";
import ModelsTable from "../shared/ModelsTable";

const Models: React.FC = () => {
  const { apiKey } = usePermissions();
  const [modelData, setModelData] = useState<Model[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [showDisabled, setShowDisabled] = useState(false);
  const navigate = useNavigate();

  const fetchData = () => {
    FinetuningService.getFinetunedModels(setIsLoading, apiKey).subscribe(
      (data: Model[]) => {
        setModelData(data);
      },
      (error: Error) => {
        console.error("Error fetching model data:", error);
        notification.error({
          message: "Error",
          description: "Failed to load models",
        });
      }
    );
  };

  useEffect(() => {
    fetchData();
  }, [apiKey]);

  const filteredData = useMemo(() => {
    if (showDisabled) return modelData;
    return modelData.filter((m) => m.status?.toUpperCase() === "ENABLED");
  }, [modelData, showDisabled]);

  // Group models by model name, then flatten for pagination
  const processedData = useMemo(() => {
    const groups = filteredData.reduce(
      (acc: { [key: string]: Model[] }, model) => {
        if (!acc[model.model]) {
          acc[model.model] = [];
        }
        acc[model.model].push(model);
        return acc;
      },
      {}
    );

    return Object.entries(groups).flatMap(([modelName, models]) => {
      return models.map((model) => ({
        ...model,
        key: model.id,
      }));
    });
  }, [filteredData]);

  // Pagination logic
  const totalItems = processedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentPageData = processedData.slice(startIndex, startIndex + pageSize);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Models</span>
                </li>
              </ol>
            </nav>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Models
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manage your fine-tuned models
                </p>
              </div>
              <Button
                onClick={() => navigate("/forge/finetuning/new")}
                className="gap-2"
              >
                <PlusIcon className="w-4 h-4" />
                New Model
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Checkbox
            id="show-disabled"
            checked={showDisabled}
            onCheckedChange={(v) => {
              setShowDisabled(!!v);
              setCurrentPage(1);
            }}
          />
          <Label
            htmlFor="show-disabled"
            className="text-sm text-muted-foreground font-gc-reg cursor-pointer"
          >
            Show disabled
          </Label>
        </div>

        <ModelsTable
          models={currentPageData}
          loading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
          onModelNameClick={(model) => {
            if (model.finetune_run_id) {
              navigate(`/forge/finetuning/${model.finetune_run_id}`);
            }
          }}
          emptyTitle="No fine-tuned models found"
          emptyDescription="Create a new fine-tuned model by starting a finetune run."
        />
      </div>
    </div>
  );
};

export default Models;
